/**
 * Environment Configuration
 *
 * All external URLs and environment-specific settings are centralized here.
 * Values are read from environment variables set at build time.
 *
 * Usage:
 *   import { ENV } from '../configs/environment';
 *   console.log(ENV.API_URL);
 */

const ENV = {
  // Environment name
  ENV_NAME: process.env.REACT_APP_ENV_NAME || "development",

  // API
  API_URL: process.env.REACT_APP_API_URL || "https://api-prod.seniorlife.ai/",

  // Authentication
  SIGNIN_URL:
    process.env.REACT_APP_SIGNIN_URL || "https://signin.dev.seniorlife.io",

  // Test Persons Management
  TEST_PERSONS_URL:
    process.env.REACT_APP_TEST_PERSONS_URL ||
    "https://testpersons.dev.seniorlife.io",

  // Reports
  REPORTS_URL:
    process.env.REACT_APP_REPORTS_URL || "https://report.seniorlife.ai",

  // Marketing Website
  WEBSITE_URL: process.env.REACT_APP_WEBSITE_URL || "https://www.seniorlife.io",

  // Staff Portal Origin (for isProd detection fallback)
  STAFF_PORTAL_URL:
    process.env.REACT_APP_STAFF_PORTAL_URL || "https://staff.dev.seniorlife.io",

  // Service Dashboard (admin portal)
  DASHBOARD_URL:
    process.env.REACT_APP_DASHBOARD_URL || "https://dashboard.seniorlife.ai",

  // Activity Monitoring API
  ACTIVITY_MONITORING_API_URL:
    process.env.REACT_APP_ACTIVITY_MONITORING_API_URL ||
    "http://57.154.209.228:3000",

  // V5 API
  V5_API_URL:
    process.env.REACT_APP_V5_API_URL || "https://api-prod.seniorlife.ai",

  // Feature Flags (can also be environment-specific)
  ENABLE_VIDEO_RECORDING:
    process.env.REACT_APP_ENABLE_VIDEO_RECORDING === "true",
  ENABLE_V2_CARE_PLAN: process.env.REACT_APP_ENABLE_V2_CARE_PLAN === "true",

  // Azure Storage
  AZURE_STORAGE_ACCOUNT_NAME:
    process.env.REACT_APP_AZURE_STORAGE_ACCOUNT_NAME || "stseniorlifeeastus",
  AZURE_STORAGE_CONTAINER_NAME:
    process.env.REACT_APP_AZURE_STORAGE_CONTAINER_NAME || "sl-private-staging",

  // MQTT
  MQTT_BROKER_URL:
    process.env.REACT_APP_MQTT_BROKER_URL ||
    "mqtt://mqtt.dev.seniorlife.io:1883",
  MQTT_BROKER_WSS_URL:
    process.env.REACT_APP_MQTT_BROKER_WSS_URL ||
    "wss://mqtt.dev.seniorlife.io:8884/mqtt",

  // Firebase Configuration
  FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  FIREBASE_VAPID_KEY: process.env.REACT_APP_FIREBASE_VAPID_KEY,
};

// Computed values
const isProd = ENV.ENV_NAME === "production";

// URL Builders
const getSigninUrl = (locale) => `${ENV.SIGNIN_URL}/login?locale=${locale}`;

const getTestPersonsUrl = (path, params) =>
  `${ENV.TEST_PERSONS_URL}${path}${params}`;

const JA_WEBSITE_URL = "https://jp.seniorlife.ai";

const buildWebsiteUrl = (locale, enPath, jaPath) => {
  if (locale === "ja") return `${JA_WEBSITE_URL}/${jaPath ?? enPath}`;
  return `${ENV.WEBSITE_URL}/${enPath}`;
};

const getPrivacyPolicyUrl = (locale) =>
  buildWebsiteUrl(locale, "privacy-data-policy/", "privacy-policy/");

const getTermsUrl = (locale) =>
  buildWebsiteUrl(locale, "terms-of-service/", "terms-conditions/");

const getAboutUsUrl = (locale) => buildWebsiteUrl(locale, "about-us/");

const getContactUsUrl = (locale) => buildWebsiteUrl(locale, "contact-us/");

/**
 * Rewrite the host/origin of a report URL to ENV.REPORTS_URL.
 *
 * Backend responses (e.g. reportURL, wellness_report_url) may still carry the
 * legacy `rom-report.*` origin. This helper forces all outbound navigations to
 * use the configured REPORTS_URL (e.g. https://report.seniorlife.ai) while
 * preserving the existing path, query string and hash.
 *
 * Returns the original value unchanged if it is not a parseable absolute URL
 * or if REPORTS_URL is not configured.
 */
const rewriteReportDomain = (rawUrl) => {
  if (!rawUrl || !ENV.REPORTS_URL) return rawUrl;
  try {
    const target = new URL(ENV.REPORTS_URL);
    const incoming = new URL(rawUrl);
    incoming.protocol = target.protocol;
    incoming.host = target.host;
    return incoming.toString();
  } catch {
    // rawUrl wasn't an absolute URL (e.g. relative path). Leave it alone.
    return rawUrl;
  }
};

export {
  ENV,
  isProd,
  getSigninUrl,
  getTestPersonsUrl,
  getPrivacyPolicyUrl,
  getTermsUrl,
  getAboutUsUrl,
  getContactUsUrl,
  rewriteReportDomain,
};
